// API Configuration for AI Commerce OS  
// Production Railway URL: https://web-production-3fcca.up.railway.app

// Detect if we're on the deployed site vs local development.
// melamarketplace.app is the Mela-only domain; idliapam.in serves admin +
// the rest of the product portfolio. Both are production hosts.
const PRODUCTION_HOSTS = [
  'idliapam.in',
  'www.idliapam.in',
  'melamarketplace.app',
  'www.melamarketplace.app',
  'melamarketplace.com',
  'www.melamarketplace.com',
];
const isProduction = PRODUCTION_HOSTS.indexOf(window.location.hostname) >= 0;
const RAILWAY_URL = 'https://web-production-3fcca.up.railway.app';
const LOCAL_URL = 'http://localhost:8082';

export const API_CONFIG = {
  BASE_URL: (process.env.REACT_APP_API_URL || RAILWAY_URL).replace(/\/+$/, ''), // Strip trailing slashes
  FALLBACK_URL: LOCAL_URL, // Fallback for development
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  
  // API Endpoints
  ENDPOINTS: {
    // Authentication
    AUTH: {
      LOGIN: '/api/v1/auth/login',
      REGISTER: '/api/v1/auth/register',
      REFRESH: '/api/v1/auth/refresh',
      LOGOUT: '/api/v1/auth/logout',
      PROFILE: '/api/v1/auth/me'
    },
    
    // Documents & Vector Database
    DOCUMENTS: {
      LIST: '/api/v1/documents',
      UPLOAD: '/api/v1/documents/upload',
      UPLOAD_URL: '/api/v1/documents/upload-url',
      DELETE: '/api/v1/documents/{id}',
      PROCESS: '/api/v1/documents/{id}/process',
      STATUS: '/api/v1/documents/status',
      SEARCH: '/api/v1/documents/search',
      COLLECTIONS: '/api/v1/documents/collections'
    },
    
    // Chat & AI Agents
    CHAT: {
      BRAND_COPILOT: '/api/v1/chat/brand-copilot',
      SHOPPER_CONCIERGE: '/api/v1/chat/shopper-concierge',
      CONVERSATIONS: '/api/v1/chat/conversations',
      HISTORY: '/api/v1/chat/conversations/{id}/messages'
    },
    
    // Real-time
    REALTIME: {
      CHAT: '/api/v1/realtime/chat',
      CONVERSATIONS: '/api/v1/realtime/chat/conversations',
      WEBSOCKET: '/api/v1/ws/connect'
    },
    
    // Analytics
    ANALYTICS: {
      DASHBOARD: '/api/v1/analytics/dashboard',
      EVENTS: '/api/v1/analytics/events',
      FUNNEL: '/api/v1/analytics/funnel',
      GENERATE_INSIGHTS: '/api/v1/analytics/generate-insights'
    },
    
    // Products
    PRODUCTS: {
      LIST: '/api/v1/products',
      SEARCH: '/api/v1/products/search',
      BUNDLES: '/api/v1/products/bundles'
    },
    
    // Brands
    BRANDS: {
      LIST: '/api/v1/brands',
      CREATE: '/api/v1/brands',
      GET: '/api/v1/brands/{id}',
      STATS: '/api/v1/brands/{id}/stats'
    },
    
    // Brand Management
    BRAND: {
      LOGO_UPLOAD: '/api/v1/brand/logo/upload',
      LOGO_GET: '/api/v1/brand/logo',
      LOGO_DELETE: '/api/v1/brand/logo/{id}'
    },
    
    // Proposal Automation
    PROPOSALS: {
      TRIGGER: '/api/v1/proposals/trigger-automation',
      STATUS: '/api/v1/proposals/automations/{id}',
      LIST: '/api/v1/proposals/automations',
      TEMPLATES: '/api/v1/proposals/templates',
      ANALYTICS: '/api/v1/proposals/analytics',
      UPDATE_LEAD_STAGE: '/api/v1/proposals/leads/{id}/update-stage'
    },
    
    // Workflows
    WORKFLOWS: {
      LIST: '/api/v1/workflows',
      CREATE: '/api/v1/workflows',
      GET: '/api/v1/workflows/{id}',
      UPDATE: '/api/v1/workflows/{id}',
      DELETE: '/api/v1/workflows/{id}',
      DUPLICATE: '/api/v1/workflows/{id}/duplicate',
      EXECUTE: '/api/v1/workflows/{id}/execute',
      VERSIONS: '/api/v1/workflows/{id}/versions',
      ANALYTICS: '/api/v1/workflows/analytics'
    },
    
    // Workflow Templates
    WORKFLOW_TEMPLATES: {
      LIST: '/api/v1/workflow-templates',
      CREATE: '/api/v1/workflow-templates',
      GET: '/api/v1/workflow-templates/{id}',
      UPDATE: '/api/v1/workflow-templates/{id}',
      DELETE: '/api/v1/workflow-templates/{id}',
      DUPLICATE: '/api/v1/workflow-templates/{id}/duplicate'
    },
    
    // Account Management & Billing
    ACCOUNT: {
      INVOICES: '/api/v1/account/invoices',
      INVOICE_DETAIL: '/api/v1/account/invoices/{id}',
      INVOICE_SEND: '/api/v1/account/invoices/{id}/send',
      INVOICE_PAY: '/api/v1/account/invoices/{id}/pay',
      INVOICE_REMINDER: '/api/v1/account/invoices/{id}/reminder',
      ANALYTICS: '/api/v1/account/analytics',
      CUSTOMERS: '/api/v1/account/customers',
      SUBSCRIPTION: '/api/v1/account/subscription',
      USAGE: '/api/v1/account/usage',
      PAYMENT_METHODS: '/api/v1/billing/payment-methods'
    },
    
    // Brand Copilot AI (Railway PostgreSQL with real data)
    COPILOT: {
      QUERY: '/api/v1/copilot/query',
      HEALTH: '/api/v1/copilot/health',
      DATA_OVERVIEW: '/api/v1/copilot/data-overview'
    },
    
    // Advanced Insights & Analytics (AI-powered with ML predictions)
    INSIGHTS: {
      GENERATE: '/api/v1/insights/generate',
      REVENUE_ANALYTICS: '/api/v1/insights/revenue-analytics',
      PREDICTIONS: '/api/v1/insights/predictions',
      RECOMMENDATIONS: '/api/v1/insights/recommendations',
      SENTIMENT: '/api/v1/insights/sentiment',
      OVERVIEW: '/api/v1/insights/overview'
    },
    
    // System
    SYSTEM: {
      HEALTH: '/health',
      TEST_BROADCAST: '/api/v1/ws/test-broadcast',
      SETUP_STATUS: '/setup-status'
    }
  }
};

// Request/Response Types
export interface APIResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  success?: boolean;
  status?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface APIError {
  message: string;
  code?: string;
  details?: any;
}

// WebSocket Configuration
export const WS_CONFIG = {
  BASE_URL: (process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app').replace('http://', 'ws://').replace('https://', 'wss://'),
  RECONNECT_ATTEMPTS: 5,
  RECONNECT_INTERVAL: 3000,
  PING_INTERVAL: 30000
};